import { Currency } from '@/lib/entities/currency';
import {
  RENT_INCLUDED_SERVICE_KEYS,
  RENT_PAID_SERVICE_KEYS,
  RentSelectionLayout,
} from '@/lib/entities/page/model/rent-selection';

import { convertByRates } from '@/lib/shared/utils/convert-by-rates';
import { getWordEnding } from '@/lib/shared/utils/get-word-ending';
import { toMoney } from '@/lib/shared/utils/to-money';

const BOOKING_SYMBOLS: Record<string, string> = {
  usd: '$',
  thb: '฿',
  rub: '₽',
  eur: '€',
};

const PLACEHOLDER = '/images/placeholder_image.webp';

const ALLOWED_INCLUDED = new Set<string>(RENT_INCLUDED_SERVICE_KEYS);
const ALLOWED_PAID = new Set<string>(RENT_PAID_SERVICE_KEYS);

const formatIsoDate = (value?: string | null) => {
  if (!value) return '';
  const [year, month, day] = value.slice(0, 10).split('-');
  if (!year || !month || !day) return '';
  return `${day}.${month}.${year}`;
};

const getObjectHref = (publicUrl: string, language: string) => {
  if (/^https?:\/\//.test(publicUrl)) return publicUrl;
  if (publicUrl.startsWith('/en/') || publicUrl.startsWith('/ru/')) {
    return publicUrl;
  }
  if (publicUrl.startsWith('/')) return `/${language}${publicUrl}`;
  return `/${language}/${publicUrl}`;
};

const getBookingSymbol = (currency?: string | null, fallback = '$') => {
  if (!currency) return fallback;
  return BOOKING_SYMBOLS[currency.toLowerCase()] || currency;
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const getRentSelectionCardView = (
  layout: RentSelectionLayout,
  t: Translate,
  language: string,
  siteSymbol: string,
  siteCurrency: Currency['label'],
  rates: Record<string, number>
) => {
  const coverImage = layout.images?.find(Boolean) || PLACEHOLDER;
  const districtsString = (layout.districts || [])
    .map((item) => t(`locations.${item}`))
    .join(', ');
  const location = districtsString
    ? `${t(`locations.${layout.region}`)}, ${districtsString}`
    : t(`locations.${layout.region}`);

  const beach = layout.distanceToBeach;
  const beachText =
    beach?.beach && beach.minutes != null
      ? `${t(`locations.${beach.beach}`)}, ${beach.minutes} ${getWordEnding(
          beach.minutes,
          [
            t('declinable.minute'),
            t('declinable.minutes'),
            t('declinable.minutesMore'),
          ],
          language
        )} ${beach.method === 'FOOT' ? t('byFoot') : t('byCar')}`
      : '';

  const views = [...(layout.views || [])];
  if (views.includes('OTHER')) {
    views.splice(views.indexOf('OTHER'), 1);
    views.push('OTHER');
  }
  const viewsText = views
    .map((item) => {
      const label = t(item);
      if (language === 'ru') {
        return `${t('prepositions.on')} ${label}`;
      }
      return label;
    })
    .join(', ');

  const dateFrom = formatIsoDate(layout.dateFrom);
  const dateTo = formatIsoDate(layout.dateTo);
  const bookingDates =
    dateFrom && dateTo
      ? `${t('prepositions.fromThe')} ${dateFrom} ${t('prepositions.toThe')} ${dateTo}`
      : '';

  const bookingNight =
    layout.priceNight != null && layout.priceNight > 0
      ? layout.priceNight
      : null;
  const bookingTotal =
    layout.total != null && layout.total > 0 ? layout.total : null;
  const convertedNight =
    bookingNight == null
      ? null
      : convertByRates(bookingNight, layout.currency, siteCurrency, rates);
  const convertedTotal =
    bookingTotal == null
      ? null
      : convertByRates(bookingTotal, layout.currency, siteCurrency, rates);
  const nightValue = convertedNight ?? bookingNight;
  const totalValue = convertedTotal ?? bookingTotal;
  const nightSymbol =
    convertedNight != null
      ? siteSymbol
      : getBookingSymbol(layout.currency, siteSymbol);
  const totalSymbol =
    convertedTotal != null
      ? siteSymbol
      : getBookingSymbol(layout.currency, siteSymbol);

  const nightLabel =
    nightValue != null
      ? `${nightSymbol} ${toMoney(nightValue) ?? nightValue}`
      : '';
  const nightsWord = getWordEnding(
    layout.nights || 0,
    [
      t('declinable.night'),
      t('declinable.nightsFew'),
      t('declinable.nightsMore'),
    ],
    language
  );
  const objectHref = layout.publicUrl
    ? getObjectHref(layout.publicUrl, language)
    : null;
  const commentText = layout.comment?.replace(/\u2028|\u2029/g, '\n');

  return {
    coverImage,
    location,
    beachText,
    viewsText,
    bookingDates,
    nightLabel,
    nightsWord,
    totalValue,
    totalSymbol,
    objectHref,
    commentText,
    includedServices: (layout.includedServices ?? []).filter((key) =>
      ALLOWED_INCLUDED.has(key)
    ),
    paidServices: (layout.paidServices ?? []).filter((item) =>
      ALLOWED_PAID.has(item.key)
    ),
  };
};
